// Arbiter-owned player databases: Firestore access plus the local cache that
// makes searching them instant.
//
// SHAPE (see firestore.rules for the matching access rules)
//   playerDatabases/{dbId}                 meta + the rating list configs (lists[])
//   playerDatabases/{dbId}/players/{id}    one document per person
//   playerDatabases/{dbId}/events/{id}     applied rating reports (Cloud Function only)
//
// WHY A LOCAL CACHE
// The Add Player search has to answer on every keystroke. Re-querying Firestore
// for that would be both slow and expensive (a club list is thousands of
// documents), so the whole player list is mirrored into IndexedDB once and then
// kept fresh with an INCREMENTAL query on `updatedAt` — a returning session pays
// for the handful of players that changed, not the whole list. Search itself
// never touches the network.
//
// WHY RATINGS ARE NEVER WRITTEN HERE
// The `ratings` map is pinned by the security rules on update: it moves only
// through the applyRatingReport Cloud Function, which writes the matching audit
// event in the same batch. updatePlayer() therefore strips `ratings` before
// writing rather than relying on callers to remember — a stripped field is a
// no-op, whereas sending one is a permission-denied that fails the whole save.

import {
    collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
    query, where, orderBy, limit, onSnapshot, writeBatch, arrayUnion, arrayRemove
} from "https://www.gstatic.com/firebasejs/12.12.1/firebase-firestore.js";
import { db, auth, lookupArbiterByEmail } from "./firebase-auth.js";
import {
    formatDbPlayerName, normalizePlayer,
    searchPlayers as searchPlayerRecords, eventIdFor, findRatingList
} from "../utils/player-db-shape.js";
import { offlineError } from "../utils/offline-guards.js";

// Re-exported so callers have one import for "everything about player databases"
// (the split exists only so the pure half can be unit-tested — see that file).
export { formatDbPlayerName, normalizePlayer, eventIdFor, findRatingList };

const DBS = 'playerDatabases';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function currentUid() {
    return auth.currentUser ? auth.currentUser.uid : null;
}

function currentEmail() {
    const u = auth.currentUser;
    return u && u.email ? u.email.trim().toLowerCase() : null;
}

function requireAuth() {
    const uid = currentUid();
    if (!uid) throw new Error('Not signed in');
    return uid;
}

// ---------------------------------------------------------------------------
// Databases
// ---------------------------------------------------------------------------

/**
 * Every database this arbiter can reach: the ones they own, plus the ones they
 * have been added to (or invited to) as a cooperating arbiter.
 *
 * Three queries rather than one because Firestore cannot OR across different
 * fields; results are merged by id. Ownership is checked first so an owner who is
 * also (somehow) listed as a co-arbiter is reported as the owner.
 */
export async function listMyDatabases() {
    const uid = requireAuth();
    const email = currentEmail();
    const col = collection(db, DBS);

    const queries = [getDocs(query(col, where('ownerId', '==', uid)))];
    if (email) {
        queries.push(getDocs(query(col, where('cooperatingArbiters', 'array-contains', email))));
        queries.push(getDocs(query(col, where('pendingCooperatingArbiters', 'array-contains', email))));
    }

    const snaps = await Promise.all(queries.map(p => p.catch(() => null)));
    const byId = new Map();
    snaps.forEach((snap, i) => {
        if (!snap) return;
        snap.docs.forEach(d => {
            if (byId.has(d.id)) return;
            byId.set(d.id, { id: d.id, ...d.data(), role: i === 0 ? 'owner' : (i === 1 ? 'arbiter' : 'pending') });
        });
    });
    return Array.from(byId.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

export async function getDatabase(dbId) {
    const snap = await getDoc(doc(db, DBS, String(dbId)));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function listenToDatabase(dbId, onChange) {
    return onSnapshot(doc(db, DBS, String(dbId)), snap => {
        onChange(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
}

// A database is never public. It is shared with named arbiters instead (see the
// sharing section below), so no isPublic flag is written.
export async function createDatabase({ name, description = '' } = {}) {
    const uid = requireAuth();
    const dbId = 'pdb_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);
    const now = Date.now();
    const record = {
        name: (name || '').trim() || 'Ny database',
        description: (description || '').trim(),
        ownerId: uid,
        ownerEmail: currentEmail() || '',
        cooperatingArbiters: [],
        pendingCooperatingArbiters: [],
        lists: [],
        playerCount: 0,
        createdAt: now,
        lastUpdated: now
    };
    await setDoc(doc(db, DBS, dbId), record);
    return { id: dbId, ...record };
}

// Patch the meta doc. Never send ownerId, ownerEmail or either arbiter list from
// here — the sharing functions below own those, and the rules reject a co-arbiter
// who touches them. isPublic is dropped too: it no longer means anything.
export async function updateDatabase(dbId, patch) {
    requireAuth();
    const clean = { ...patch };
    delete clean.ownerId;
    delete clean.ownerEmail;
    delete clean.cooperatingArbiters;
    delete clean.pendingCooperatingArbiters;
    delete clean.isPublic;
    clean.lastUpdated = Date.now();
    await updateDoc(doc(db, DBS, String(dbId)), clean);
}

/**
 * Delete a database and everything under it.
 *
 * Subcollections are NOT removed by deleting the parent in Firestore, so the
 * players and events are paged through and batch-deleted first; the meta doc goes
 * last, so an interrupted delete leaves a still-reachable (if partly emptied)
 * database rather than orphaned subcollections nobody can see or clean up.
 */
export async function deleteDatabase(dbId) {
    requireAuth();
    const id = String(dbId);

    // Tell everyone it was shared with, and withdraw any invitation still waiting,
    // while the meta doc still exists: the rule that lets the owner clear those
    // invite notifications resolves ownership through it. Best-effort — a failure
    // here must not leave the owner unable to delete their own database.
    try {
        const snap = await getDoc(doc(db, DBS, id));
        if (snap.exists()) {
            const data = snap.data();
            const recipients = [...new Set(
                [...(data.cooperatingArbiters || []), ...(data.pendingCooperatingArbiters || [])]
                    .map(normEmail).filter(Boolean)
            )];
            await Promise.all(recipients.map(email => sendShareNotification(email, 'db_deleted', id, data.name)));
            await clearInviteNotifications(id, data.pendingCooperatingArbiters || []);
        }
    } catch (e) {
        console.warn('Deleting a database: could not notify its arbiters:', e);
    }

    await deleteSubcollection(id, 'players');
    await deleteSubcollection(id, 'events');
    await deleteDoc(doc(db, DBS, id));
    await clearCachedPlayers(id);
}

// ---------------------------------------------------------------------------
// Sharing with other arbiters
//
// The same model as sharing a tournament (inviteArbiterByEmail and friends in
// js/services/firebase-auth.js): the owner invites a registered arbiter by email,
// the email goes into pendingCooperatingArbiters, and a notification is sent. The
// invitee accepts or declines - from the notification or from the card in their
// database registry - which moves their email into cooperatingArbiters or drops
// it. The rules (firestore.rules, playerDatabases) enforce every transition, so
// the checks here exist for a clear message, not for security.
//
// Notification types are distinct from the tournament ones (db_share_*), so a
// database invitation can never be routed into the tournament accept handler.
// ---------------------------------------------------------------------------

function normEmail(email) {
    return (email || '').trim().toLowerCase();
}

function newNotificationId() {
    return Date.now().toString() + '_' + Math.random().toString(36).substring(2, 8);
}

function sendShareNotification(recipientEmail, type, dbId, dbName) {
    const user = auth.currentUser;
    return setDoc(doc(db, 'notifications', newNotificationId()), {
        recipientEmail: normEmail(recipientEmail),
        senderUid: user.uid,
        senderEmail: user.email,
        senderName: user.displayName || (user.email || '').split('@')[0],
        databaseId: String(dbId),
        databaseName: dbName || '',
        type,
        status: 'pending',
        timestamp: new Date().toISOString()
    });
}

// Clear outstanding db_share_invite notifications for this database: the
// invitee's own when they answer, or - as the owner - the ones sent to the given
// addresses. Best-effort: the arbiter lists are the authority, and a leftover
// notification only offers an Accept the rules will refuse.
async function clearInviteNotifications(dbId, emails) {
    await Promise.all((emails || []).map(normEmail).filter(Boolean).map(async (email) => {
        try {
            const snap = await getDocs(query(
                collection(db, 'notifications'),
                where('recipientEmail', '==', email),
                where('databaseId', '==', String(dbId)),
                where('type', '==', 'db_share_invite')
            ));
            await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
        } catch (e) {
            console.warn('Could not clear a database invitation notification:', e);
        }
    }));
}

async function readOwnedDatabase(dbId) {
    const uid = requireAuth();
    const snap = await getDoc(doc(db, DBS, String(dbId)));
    if (!snap.exists()) throw new Error('That database no longer exists.');
    const data = snap.data();
    if (data.ownerId !== uid) throw new Error('Only the owner of a database can change who it is shared with.');
    return data;
}

/** Invite a registered arbiter to share a database. Owner only. */
export async function inviteDatabaseArbiter(dbId, email) {
    const target = normEmail(email);
    if (!target) throw new Error('Please enter a valid email address.');
    if (target === currentEmail()) throw new Error('You cannot share a database with yourself.');

    const data = await readOwnedDatabase(dbId);
    if ((data.cooperatingArbiters || []).map(normEmail).includes(target)) {
        throw new Error('This arbiter already shares this database.');
    }
    if ((data.pendingCooperatingArbiters || []).map(normEmail).includes(target)) {
        throw new Error('This arbiter has already been invited and has not answered yet.');
    }

    const lookup = await lookupArbiterByEmail(target);
    if (!lookup.exists) throw new Error('No registered account found with this email address.');
    if (lookup.role === 'player') {
        throw new Error('This email is registered with a Player account. You can only share a database with arbiters.');
    }

    await updateDoc(doc(db, DBS, String(dbId)), { pendingCooperatingArbiters: arrayUnion(target) });
    await sendShareNotification(target, 'db_share_invite', dbId, data.name);
    return target;
}

/** Withdraw an invitation that has not been answered yet. Owner only. */
export async function retractDatabaseInvite(dbId, email) {
    const target = normEmail(email);
    await readOwnedDatabase(dbId);
    // The list first: it is what the rules check, so once it is done the invitation
    // is dead even if clearing the notification below fails.
    await updateDoc(doc(db, DBS, String(dbId)), { pendingCooperatingArbiters: arrayRemove(target) });
    await clearInviteNotifications(dbId, [target]);
    return target;
}

/** Stop sharing with an arbiter who had accepted. Owner only. */
export async function removeDatabaseArbiter(dbId, email) {
    const target = normEmail(email);
    const data = await readOwnedDatabase(dbId);
    await updateDoc(doc(db, DBS, String(dbId)), {
        cooperatingArbiters: arrayRemove(target),
        pendingCooperatingArbiters: arrayRemove(target)
    });
    await sendShareNotification(target, 'db_share_removed', dbId, data.name);
    return target;
}

// Accept or decline, as the invitee. Both write ONLY the two arbiter lists - the
// rules refuse anything else from someone who has not accepted yet. The list
// change comes first and the notification clean-up after, so a failure (most
// likely an invitation retracted in the meantime) never destroys the invitee's
// way back into the flow.
async function answerDatabaseInvite(dbId, accept) {
    requireAuth();
    const me = currentEmail();
    const ref = doc(db, DBS, String(dbId));
    // Read BEFORE answering: once declined, the invitee can no longer read the
    // meta doc, and the reply needs the owner's address and the database name.
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('That database no longer exists.');
    const data = snap.data();

    await updateDoc(ref, accept
        ? { pendingCooperatingArbiters: arrayRemove(me), cooperatingArbiters: arrayUnion(me) }
        : { pendingCooperatingArbiters: arrayRemove(me) });

    await clearInviteNotifications(dbId, [me]);
    if (data.ownerEmail) {
        try {
            await sendShareNotification(data.ownerEmail, accept ? 'db_share_accepted' : 'db_share_declined', dbId, data.name);
        } catch (e) {
            console.warn('Could not tell the owner about the answer:', e);
        }
    }
}

export function acceptDatabaseInvite(dbId) {
    return answerDatabaseInvite(dbId, true);
}

export function declineDatabaseInvite(dbId) {
    return answerDatabaseInvite(dbId, false);
}

async function deleteSubcollection(dbId, name) {
    const col = collection(db, DBS, dbId, name);
    // 400 per batch: Firestore's limit is 500 writes, and the margin leaves room
    // for this to stay correct if a future change adds a write to the loop.
    for (;;) {
        const snap = await getDocs(query(col, limit(400)));
        // Offline this listing is the cache, and "empty" would let the database
        // document itself be deleted over players still in the cloud — orphaned,
        // with nothing left that could find them. Stop instead; a second attempt
        // with a connection finishes the job.
        if (snap.metadata.fromCache) throw offlineError('Deleting a database needs an internet connection.');
        if (snap.empty) return;
        const batch = writeBatch(db);
        snap.docs.forEach(d => batch.delete(d.ref));
        await batch.commit();
        if (snap.size < 400) return;
    }
}

// ---------------------------------------------------------------------------
// Rating lists (stored in the meta doc's `lists` array)
// ---------------------------------------------------------------------------

// The configs are small and always needed together with the database itself, so
// they live in the meta doc rather than a subcollection. Upsert by id.
export async function saveRatingList(dbId, listConfig) {
    requireAuth();
    const database = await getDatabase(dbId);
    if (!database) throw new Error('Database not found');

    const lists = Array.isArray(database.lists) ? database.lists.slice() : [];
    const cfg = { ...listConfig };
    if (!cfg.id) cfg.id = 'list_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6);

    const idx = lists.findIndex(l => l && l.id === cfg.id);
    if (idx === -1) lists.push(cfg);
    else lists[idx] = cfg;

    await updateDatabase(dbId, { lists });
    // The standings column computes from this config, so a stale copy would keep
    // showing rating changes under the OLD rules until a reload.
    invalidateListConfig(dbId, cfg.id);
    return cfg;
}

/**
 * Remove a rating list config.
 *
 * The ratings players already hold under that list id are left alone on purpose:
 * deleting a config must not silently rewrite player documents (which these rules
 * would reject anyway), and an accidental delete is then recoverable by
 * re-creating the list with the same id.
 */
export async function deleteRatingList(dbId, listId) {
    requireAuth();
    const database = await getDatabase(dbId);
    if (!database) return;
    const lists = (Array.isArray(database.lists) ? database.lists : []).filter(l => l && l.id !== listId);
    await updateDatabase(dbId, { lists });
    invalidateListConfig(dbId, listId);
}

// --- Bound-list config cache -------------------------------------------------
// The standings table renders synchronously but needs the bound rating list's
// rules to compute its rating-change column, and the config lives in a Firestore
// document. Fetching mid-render is not possible, so configs are memoized here:
// the render reads whatever is cached (null on the very first paint, which falls
// back to FIDE) and asks for one re-render when it lands.

const listConfigCache = new Map();      // "dbId|listId" -> config | null
const listConfigInFlight = new Map();

const configKey = (dbId, listId) => String(dbId) + '|' + String(listId);

/** The cached config, or null if it is not loaded yet. Never blocks, never throws. */
export function getCachedListConfig(dbId, listId) {
    if (!dbId || !listId) return null;
    const hit = listConfigCache.get(configKey(dbId, listId));
    return hit === undefined ? null : hit;
}

/**
 * Load and cache one list's config. Concurrent callers share a single fetch.
 * Resolves to the config, or null when the database or list no longer exists.
 */
export async function ensureListConfig(dbId, listId) {
    if (!dbId || !listId) return null;
    const key = configKey(dbId, listId);
    if (listConfigCache.has(key)) return listConfigCache.get(key);
    if (listConfigInFlight.has(key)) return listConfigInFlight.get(key);

    const request = (async () => {
        try {
            const config = findRatingList(await getDatabase(dbId), listId);
            listConfigCache.set(key, config);
            return config;
        } catch (e) {
            // A failed load is NOT cached: caching it as "no such list" would pin
            // the standings column to FIDE rules for the rest of the session.
            // Leaving it absent lets the next render retry.
            return null;
        } finally {
            listConfigInFlight.delete(key);
        }
    })();

    listConfigInFlight.set(key, request);
    return request;
}

/** Drop a cached config after its list is edited, so the next render re-reads it. */
export function invalidateListConfig(dbId, listId) {
    if (dbId && listId) listConfigCache.delete(configKey(dbId, listId));
    else listConfigCache.clear();
}

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------

export async function createPlayer(dbId, player) {
    requireAuth();
    const playerId = 'p_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);
    const now = Date.now();
    const record = {
        ...normalizePlayer(player),
        // A create MAY state opening ratings (importing a club's existing list);
        // see the rules comment. Every later change goes through the function.
        ratings: (player && player.ratings && typeof player.ratings === 'object') ? player.ratings : {},
        createdAt: now,
        updatedAt: now
    };
    await setDoc(doc(db, DBS, String(dbId), 'players', playerId), record);
    await putCachedPlayer(dbId, { id: playerId, ...record });
    return { id: playerId, ...record };
}

/**
 * Update a player's details, and optionally give them an OPENING rating in a list
 * they are currently unrated in.
 *
 * normalizePlayer strips `ratings` rather than trusting callers to, because
 * sending an existing rating back is a permission-denied that fails the whole
 * save. `addRatings` is the one sanctioned way to touch the map from the client:
 * entries are merged over the player's CURRENT map and any list they are already
 * rated in is left exactly as it is — the same thing the rules enforce, applied
 * here so a stale form can never even attempt the rejected write.
 */
export async function updatePlayer(dbId, playerId, patch, { addRatings = null } = {}) {
    requireAuth();
    const clean = { ...normalizePlayer(patch), updatedAt: Date.now() };

    if (addRatings && Object.keys(addRatings).length > 0) {
        const ref = doc(db, DBS, String(dbId), 'players', String(playerId));
        const snap = await getDoc(ref);
        const existing = (snap.exists() && snap.data().ratings) || {};
        const merged = { ...existing };
        Object.keys(addRatings).forEach(listId => {
            if (!existing[listId]) merged[listId] = addRatings[listId];
        });
        // Only send the map at all if it actually gained something.
        if (Object.keys(merged).length !== Object.keys(existing).length) clean.ratings = merged;
    }

    await updateDoc(doc(db, DBS, String(dbId), 'players', String(playerId)), clean);
    const cached = await getCachedPlayer(dbId, playerId);
    await putCachedPlayer(dbId, { ...(cached || {}), id: String(playerId), ...clean });
}

export async function deletePlayer(dbId, playerId) {
    requireAuth();
    await deleteDoc(doc(db, DBS, String(dbId), 'players', String(playerId)));
    await deleteCachedPlayer(dbId, playerId);
}

/** Read every player straight from Firestore, bypassing the cache. */
export async function fetchAllPlayers(dbId) {
    const snap = await getDocs(collection(db, DBS, String(dbId), 'players'));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Clock skew between the writing device and this one could otherwise let an
// update land with an `updatedAt` just below our high-water mark and be missed
// forever. Re-reading a few minutes of overlap on every sync costs a handful of
// documents and removes the whole class of bug.
const SYNC_OVERLAP_MS = 5 * 60 * 1000;

/**
 * Bring the local cache up to date and return the full player list.
 *
 * First call for a database reads everything; later calls read only what changed
 * since the last sync. Pass { force: true } to redo the full read.
 */
export async function syncPlayers(dbId, { force = false } = {}) {
    const id = String(dbId);
    const meta = force ? null : await getCacheMeta(id);
    const col = collection(db, DBS, id, 'players');

    // Offline, getDocs answers from Firestore's local cache instead of failing, and
    // that answer is whatever happened to be cached — often nothing. Taken as the
    // server's, a delta sync moved lastSync to "now" and every change made in the
    // meantime was skipped for good; a full sync cleared this device's list and
    // refilled it from the cache, leaving the search empty. A cache answer changes
    // nothing here: the list stays as it was, and the next sync with a connection
    // picks up from the same point.
    let fetched;
    if (meta && meta.lastSync) {
        const since = meta.lastSync - SYNC_OVERLAP_MS;
        const snap = await getDocs(query(col, where('updatedAt', '>', since)));
        if (snap.metadata.fromCache) return getCachedPlayers(id);
        fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } else {
        const snap = await getDocs(col);
        if (snap.metadata.fromCache) return getCachedPlayers(id);
        fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        await clearCachedPlayers(id);
    }

    if (fetched.length > 0) await putCachedPlayers(id, fetched);
    await setCacheMeta(id, { lastSync: Date.now() });
    return getCachedPlayers(id);
}

/** Search the cached player list (see player-db-shape.js for the semantics). */
export function searchCachedPlayers(players, queryText, max = 15) {
    return searchPlayerRecords(players, queryText, max);
}

// ---------------------------------------------------------------------------
// Events (read-only from the client)
// ---------------------------------------------------------------------------

export async function listEvents(dbId, { listId = null, max = 50 } = {}) {
    const col = collection(db, DBS, String(dbId), 'events');
    const clauses = [];
    if (listId) clauses.push(where('listId', '==', listId));
    clauses.push(orderBy('appliedAt', 'desc'));
    clauses.push(limit(max));
    const snap = await getDocs(query(col, ...clauses));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getEvent(dbId, eventId) {
    const snap = await getDoc(doc(db, DBS, String(dbId), 'events', String(eventId)));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ---------------------------------------------------------------------------
// IndexedDB cache
//
// Modelled on js/services/history-db.js: plain promise-wrapped IDB, no libraries,
// and every method degrades to a no-op when IDB is unavailable (private browsing)
// so the app keeps working with network reads only.
// ---------------------------------------------------------------------------

const IDB_NAME = 'playerDatabaseCache';
const IDB_VERSION = 1;
const STORE_PLAYERS = 'players';
const STORE_META = 'meta';

let idbPromise = null;
let idbUnavailable = false;

function openIdb() {
    if (idbUnavailable) return Promise.resolve(null);
    if (idbPromise) return idbPromise;

    idbPromise = new Promise((resolve) => {
        if (typeof indexedDB === 'undefined') {
            idbUnavailable = true;
            console.warn('IndexedDB unavailable; player database search will read from the network every time.');
            resolve(null);
            return;
        }
        let request;
        try {
            request = indexedDB.open(IDB_NAME, IDB_VERSION);
        } catch (e) {
            idbUnavailable = true;
            resolve(null);
            return;
        }
        request.onupgradeneeded = () => {
            const idb = request.result;
            if (!idb.objectStoreNames.contains(STORE_PLAYERS)) {
                // Keyed by "dbId/playerId" with a dbId index, so one database's
                // players can be read or wiped without touching another's.
                const store = idb.createObjectStore(STORE_PLAYERS, { keyPath: 'key' });
                store.createIndex('dbId', 'dbId', { unique: false });
            }
            if (!idb.objectStoreNames.contains(STORE_META)) {
                idb.createObjectStore(STORE_META, { keyPath: 'dbId' });
            }
        };
        request.onsuccess = () => {
            const idb = request.result;
            idb.onversionchange = () => idb.close();
            resolve(idb);
        };
        request.onerror = () => { idbUnavailable = true; resolve(null); };
        request.onblocked = () => { idbUnavailable = true; resolve(null); };
    });

    return idbPromise;
}

function withStore(storeName, mode, fn) {
    return openIdb().then(idb => {
        if (!idb) return null;
        return new Promise((resolve, reject) => {
            let tx;
            try {
                tx = idb.transaction(storeName, mode);
            } catch (e) {
                reject(e);
                return;
            }
            let result;
            try {
                result = fn(tx.objectStore(storeName));
            } catch (e) {
                reject(e);
                return;
            }
            tx.oncomplete = () => resolve(result && result.result !== undefined ? result.result : result);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    });
}

const cacheKey = (dbId, playerId) => String(dbId) + '/' + String(playerId);

export async function getCachedPlayers(dbId) {
    try {
        const rows = await withStore(STORE_PLAYERS, 'readonly', (store) => {
            return new Promise((resolve, reject) => {
                const req = store.index('dbId').getAll(String(dbId));
                req.onsuccess = () => resolve(req.result || []);
                req.onerror = () => reject(req.error);
            });
        });
        return (rows || []).map(r => r.player);
    } catch (e) {
        console.warn('Could not read cached players:', e);
        return [];
    }
}

async function getCachedPlayer(dbId, playerId) {
    try {
        const row = await withStore(STORE_PLAYERS, 'readonly', (store) => {
            return new Promise((resolve, reject) => {
                const req = store.get(cacheKey(dbId, playerId));
                req.onsuccess = () => resolve(req.result || null);
                req.onerror = () => reject(req.error);
            });
        });
        return row ? row.player : null;
    } catch (e) {
        return null;
    }
}

async function putCachedPlayer(dbId, player) {
    return putCachedPlayers(dbId, [player]);
}

async function putCachedPlayers(dbId, players) {
    try {
        await withStore(STORE_PLAYERS, 'readwrite', (store) => {
            players.forEach(p => {
                if (!p || p.id === undefined || p.id === null) return;
                store.put({ key: cacheKey(dbId, p.id), dbId: String(dbId), player: p });
            });
        });
        return true;
    } catch (e) {
        console.warn('Could not cache players:', e);
        return false;
    }
}

async function deleteCachedPlayer(dbId, playerId) {
    try {
        await withStore(STORE_PLAYERS, 'readwrite', (store) => store.delete(cacheKey(dbId, playerId)));
        return true;
    } catch (e) {
        return false;
    }
}

export async function clearCachedPlayers(dbId) {
    try {
        await withStore(STORE_PLAYERS, 'readwrite', (store) => {
            const req = store.index('dbId').openKeyCursor(IDBKeyRange.only(String(dbId)));
            req.onsuccess = () => {
                const cursor = req.result;
                if (!cursor) return;
                store.delete(cursor.primaryKey);
                cursor.continue();
            };
        });
        await withStore(STORE_META, 'readwrite', (store) => store.delete(String(dbId)));
        return true;
    } catch (e) {
        return false;
    }
}

async function getCacheMeta(dbId) {
    try {
        return await withStore(STORE_META, 'readonly', (store) => {
            return new Promise((resolve, reject) => {
                const req = store.get(String(dbId));
                req.onsuccess = () => resolve(req.result || null);
                req.onerror = () => reject(req.error);
            });
        });
    } catch (e) {
        return null;
    }
}

async function setCacheMeta(dbId, meta) {
    try {
        await withStore(STORE_META, 'readwrite', (store) => store.put({ dbId: String(dbId), ...meta }));
        return true;
    } catch (e) {
        return false;
    }
}
